import { ref, Ref, watch } from 'vue'
import { v4 } from 'uuid'
import { Upload } from '@/generated/graphql'
import gql from 'graphql-tag'
import axios from 'axios'
import { apolloClient } from '@/apollo'
import { inferContentType, getExtensionFromFile } from '@/composables/useContentTypes'

type UploadInProgress = {
  id: string
  file: File
  fileName: string
  contentType: string
  initialized: boolean
  activelyUploading: boolean
  doneWithUploading: boolean
  finalized: boolean
  cancelled: boolean
  progress: number
  chunks: UploadingChunk[]
  url?: string
  errors: string[]
}
type UploadingChunk = {
  id: string
  uploadId: string
  part: number
  active: boolean
  done: boolean
  progress: number
  retryCount: number
  abortController?: AbortController
}
type ActiveUploads = {
  [key: string]: Promise<unknown>
}
export type OnDoneCallback = ({
  name,
  contentType,
  id,
}: {
  name: string
  contentType: string
  id: string
}) => void

const maxParallelUploads = 4
const maxRetries = 3
const chunkSize = 5 * 1024 * 1024

const uploadingChunks: ActiveUploads = {}
const addUploadsLoading = ref(false)
const uploadsInProgress = ref<UploadInProgress[]>([])
const uploadsDone = ref<Upload[]>([])
const onDoneCallbacks: OnDoneCallback[] = []

export default function useUploads(): {
  addUploadsLoading: Ref<boolean>
  uploadsInProgress: Ref<UploadInProgress[]>
  addUploads: (files: File[], fileUploadIds?: Map<File, string>) => Promise<void>
  uploadFile: (file: File) => Promise<string>
  uploadsDone: Ref<Upload[]>
  addUploadDoneListener: (callback: OnDoneCallback) => () => void
  cancelUpload: (uploadId: string) => void
  cancelAllUploads: () => void
} {
  const addUploadDoneListener = (cb: OnDoneCallback) => {
    onDoneCallbacks.push(cb)

    return () => {
      onDoneCallbacks.splice(onDoneCallbacks.indexOf(cb), 1)
    }
  }

  function getUpload(id: string): UploadInProgress | undefined {
    return uploadsInProgress.value.find((u) => u.id == id)
  }

  function recomputeUploadStats(upload: UploadInProgress) {
    upload.activelyUploading = upload.chunks.some((c) => c.active)
    upload.doneWithUploading = upload.chunks.every((c) => c.done)
    upload.progress = upload.chunks.reduce((sum, c) => sum + c.progress, 0) / upload.chunks.length
  }

  async function finalizeUpload(upload: UploadInProgress) {
    try {
      const result = await apolloClient.mutate({
        mutation: gql`
          mutation finalizeMultipartUpload($id: ID!) {
            upload {
              finalizeMultipartUpload(id: $id) {
                id
                name
                contentType
                createdAt
              }
            }
          }
        `,
        variables: { id: upload.id },
      })

      if (upload.cancelled) {
        return
      }

      const finalized = result?.data?.upload.finalizeMultipartUpload as Upload
      uploadsDone.value.push(finalized)
      uploadsInProgress.value = uploadsInProgress.value.filter((u) => u.id != upload.id)
      onDoneCallbacks.forEach((cb) => cb(finalized))
    } catch (reason) {
      const message = `Could not finalize upload: ${reason}`
      console.error(message)
      upload.errors.push(message)
    } finally {
      upload.finalized = true
    }
  }

  function processProgress(upload: UploadInProgress, chunk: UploadingChunk, progress: number) {
    if (upload.cancelled) {
      return
    }
    chunk.progress = progress
    recomputeUploadStats(upload)

    if (!upload.doneWithUploading || upload.finalized) {
      return
    }

    void finalizeUpload(upload)
  }

  function scheduleChunk(chunk: UploadingChunk) {
    const upload = getUpload(chunk.uploadId)
    if (!upload) {
      return
    }
    if (upload.cancelled) {
      return
    }
    chunk.active = true
    chunk.abortController = new AbortController()
    uploadingChunks[chunk.id] = axios
      .post(
        `/upload/multipart/${upload.id}/${chunk.part}`,
        upload.file.slice((chunk.part - 1) * chunkSize, chunk.part * chunkSize, upload.contentType),
        {
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          signal: chunk.abortController.signal,
          onUploadProgress(progressEvent) {
            if (!upload.cancelled) {
              processProgress(upload, chunk, progressEvent.progress as number)
            }
          },
        },
      )
      .then(() => {
        if (upload.cancelled) {
          return
        }
        chunk.active = false
        chunk.done = true
        processProgress(upload, chunk, 1)
      })
      .catch((reason) => {
        // Check if upload still exists and is cancelled
        const currentUpload = getUpload(chunk.uploadId)
        if (!currentUpload || currentUpload.cancelled || reason.code === 'ERR_CANCELED') {
          chunk.active = false
          chunk.done = true
          return
        }
        chunk.progress = 0
        chunk.active = false
        chunk.done = true

        if (chunk.retryCount < maxRetries) {
          console.error(`Chunk upload failed but retrying`, reason, chunk, upload)
          chunk.retryCount++
          chunk.done = false
          return
        }

        const message = `Chunk upload failed, out of retries: ${reason.message}`
        console.error(message, reason, chunk, upload)
        upload.progress = 0.0
        upload.errors.push(message)
        upload.activelyUploading = false
        upload.doneWithUploading = true
      })
      .finally(() => {
        delete uploadingChunks[chunk.id]
        scheduleChunks()
      })
  }

  function scheduleChunks() {
    const currentParallelUploads = Object.keys(uploadingChunks).length
    if (currentParallelUploads >= maxParallelUploads) {
      return
    }

    const eligibleUploads = uploadsInProgress.value.filter(
      (u) => u.initialized && !u.doneWithUploading && !u.cancelled,
    )

    const availableChunks = eligibleUploads
      .flatMap((u) => u.chunks)
      .filter((c) => !c.active && !c.done)
      .slice(0, maxParallelUploads - currentParallelUploads)

    if (availableChunks.length > 0) {
      availableChunks.forEach((c) => scheduleChunk(c))
    }
  }

  // Cancels the given upload id.
  function cancelUpload(uploadId: string) {
    const upload = getUpload(uploadId)
    if (!upload || upload.cancelled) {
      return
    }

    upload.cancelled = true
    upload.activelyUploading = false
    upload.doneWithUploading = true

    upload.chunks.forEach((chunk) => {
      if (chunk.active && chunk.abortController) {
        chunk.abortController.abort()
        delete uploadingChunks[chunk.id]
      }
      chunk.active = false
      chunk.done = true
    })

    uploadsInProgress.value = uploadsInProgress.value.filter((u) => u.id != uploadId)

    scheduleChunks()
  }

  function cancelAllUploads() {
    uploadsInProgress.value.forEach((upload) => {
      if (!upload.cancelled) {
        cancelUpload(upload.id)
      }
    })
  }

  async function createUploadingFile(file: File, uploadId: string): Promise<UploadInProgress> {
    const contentType = inferContentType(file)
    const chunkCount = Math.ceil(file.size / chunkSize)
    const chunks: UploadingChunk[] = []

    for (let i = 0; i < chunkCount; i++) {
      chunks.push({
        id: v4(),
        uploadId,
        part: i + 1,
        active: false,
        done: false,
        progress: 0.0,
        retryCount: 0,
      })
    }
    const upload: UploadInProgress = {
      id: uploadId,
      file,
      fileName: file.name,
      contentType,
      initialized: false,
      activelyUploading: false,
      doneWithUploading: false,
      finalized: false,
      cancelled: false,
      progress: 0.0,
      chunks,
      errors: [],
    }

    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation createMultipartUpload($command: CreateMultipartUpload!) {
            upload {
              createMultipartUpload(command: $command)
            }
          }
        `,
        variables: {
          command: {
            id: uploadId,
            name: file.name,
            contentType,
          },
        },
      })
      upload.initialized = true
    } catch (reason) {
      const message = `Could not create multipart upload: ${reason}`
      console.error(message)
      upload.errors.push(message)
      upload.activelyUploading = false
      upload.doneWithUploading = true
    }

    return upload
  }

  function ensureWorkableFileName(file: File): File {
    // Generic/empty filenames are browser dependent. Most common cases are the following.
    const genericBaseNames = ['image', 'paste', 'untitled', 'clipboard', 'screenshot']
    const name = file.name.toLowerCase().trim()
    if (!name) {
      return file
    }
    const base = name.includes('.') ? name.split('.').slice(0, -1).join('.') : name
    const baseIsGeneric = genericBaseNames.some((g) => base === g || base.startsWith(`${g}-`))
    if (!baseIsGeneric) {
      return file
    }

    const now = new Date()
    const date = now.toISOString().slice(0, 10)
    const time = now.toTimeString().slice(0, 8).replace(/:/g, '_')
    const ext = getExtensionFromFile(file)
    const newName = ext ? `Upload ${date} at ${time}.${ext}` : `Upload ${date} at ${time}`
    return new File([file], newName, { type: file.type, lastModified: file.lastModified })
  }

  async function addUploads(files: File[], fileUploadIds?: Map<File, string>) {
    addUploadsLoading.value = true
    const workableFiles = files.map((f) => ensureWorkableFileName(f))
    const workableFileIds = fileUploadIds
      ? new Map<File, string>(
          Array.from(fileUploadIds.entries()).map(([, id], index) => [workableFiles[index], id]),
        )
      : undefined

    for (const file of workableFiles) {
      const fileUploadId = workableFileIds?.get(file) || v4()
      const upload = await createUploadingFile(file, fileUploadId)

      if (!upload.cancelled) {
        uploadsInProgress.value.push(upload)
        if (upload.initialized && !upload.doneWithUploading) {
          setTimeout(() => {
            scheduleChunks()
          }, 0)
        }
      }
    }

    addUploadsLoading.value = false
    scheduleChunks()
  }

  function uploadFile(file: File): Promise<string> {
    const id = v4()
    return new Promise((resolve, reject) => {
      const stopWatchingErrors = watch(
        () => getUpload(id)?.errors.length ?? 0,
        (errorCount) => {
          if (!errorCount) return
          cleanUp()
          reject(new Error(getUpload(id)?.errors.join(', ')))
        },
      )
      const removeDoneListener = addUploadDoneListener((upload) => {
        if (upload.id !== id) return
        cleanUp()
        resolve(id)
      })
      const cleanUp = () => {
        stopWatchingErrors()
        removeDoneListener()
      }
      addUploads([file], new Map([[file, id]])).catch((reason) => {
        cleanUp()
        reject(reason)
      })
    })
  }

  return {
    addUploads,
    uploadFile,
    addUploadsLoading,
    uploadsInProgress,
    uploadsDone,
    addUploadDoneListener,
    cancelUpload,
    cancelAllUploads,
  }
}
